/*
 * Claim-on-open state for an instant sales task.
 *
 * Opening an instant task claims it: the server does a conditional write and the
 * first rep in wins. The losing rep must see "Already claimed by X" INSTEAD of
 * the task, not a toast over an editable form — two reps working the same member
 * is exactly what the claim exists to prevent.
 *
 * The reduction lives here, apart from form.jsx, because it is the part with
 * real branching (six outcomes over five HTTP statuses) and form.jsx has no
 * test harness.
 *
 * Fail-open is deliberate and narrow: only a 409 blocks the form. A 403 (the
 * caller does not hold SALES, e.g. a SUPER_USER reviewing the task; the form
 * skips the request for such callers once permissions have loaded)
 * is its own fail-open state: no claim, no panel, no toast, form fully usable. A 400
 * (not an instant task), a 404, a timeout or a 500 leave the rep working the
 * task they opened — a claim endpoint that is down must not make the sales queue
 * unworkable, and nothing else in the form depends on the claim having landed.
 * A 410 is not a failure: the task reached a terminal status between the list
 * render and the open, so the form goes read-only, which is what it would have
 * done had the fresh status been in the payload.
 */

import { hasAccess } from '../../../util_functions';
import { is_terminal as isTerminal } from './overdue';

export const CLAIM_IDLE = 'idle';
export const CLAIM_PENDING = 'pending';
// This rep holds the claim — either they won it, or they are re-opening a task
// they already claimed (the server treats that as idempotent).
export const CLAIM_MINE = 'mine';
// Another rep won it: the form is replaced by a blocking panel.
export const CLAIM_BLOCKED = 'blocked';
export const CLAIM_TERMINAL = 'terminal';
export const CLAIM_FAILED = 'failed';
// 403: the caller may not claim (not a SALES user). Fail-open, kept distinct from
// CLAIM_BLOCKED so it can never be mistaken for "another rep holds it".
export const CLAIM_NOT_ALLOWED = 'not_allowed';

export type ClaimStatus =
  | typeof CLAIM_IDLE
  | typeof CLAIM_PENDING
  | typeof CLAIM_MINE
  | typeof CLAIM_BLOCKED
  | typeof CLAIM_TERMINAL
  | typeof CLAIM_FAILED
  | typeof CLAIM_NOT_ALLOWED;

export interface ClaimState {
  state: ClaimStatus;
  claimed_by_ops_uuid: string;
  claimed_by_name: string;
  reason: string;
}

// The slice of goserver's FormData the claim reads.
export interface ClaimableForm {
  status?: string;
  is_instant?: boolean;
  instant_expires_at_iso?: string;
  claimed_by_ops_uuid?: string;
}

// Body of the claim endpoint's 200 `data` and of its 403/409/410 responses.
interface ClaimPayload {
  claimed_by_ops_uuid?: string;
  claimed_by_name?: string;
  reason?: string;
}

export interface ClaimResponse {
  data?: { status?: string; data?: ClaimPayload };
}

export interface ClaimError {
  response?: { status?: number; data?: ClaimPayload };
}

export interface ClaimCaller {
  permissions_status?: string;
  access_type?: string;
}

// Only a live instant task is claimable. A standard task keeps round-robin, and
// a terminal one has nothing left to claim — the server rejects both, so this
// keeps the form from making a request whose only outcome is an error.
export const should_claim_on_open = (form: ClaimableForm | null | undefined): boolean =>
  !!form && !!form.is_instant && !isTerminal(form);

// Claiming is a sales-rep action (PRD), mirroring goserver's SALES-only 403
// gate. Only trusted once permissions have loaded; before that the server decides.
export const caller_may_claim = ({
  permissions_status: permissionsStatus,
  access_type: accessType
}: ClaimCaller = {}): boolean => permissionsStatus !== 'success' || hasAccess(accessType, 'SALES');

// A claimed task never auto-completes, so its expiry is not shown. The form
// payload predates the claim, hence the claim-state check.
export const visible_instant_expiry = (
  form: ClaimableForm | null | undefined,
  claim: Pick<ClaimState, 'state'> | null | undefined
): string => {
  if (!form || !form.instant_expires_at_iso) return '';
  if (form.claimed_by_ops_uuid) return '';
  if (claim && claim.state === CLAIM_MINE) return '';
  return form.instant_expires_at_iso;
};

// 200 — this rep holds the task. The names come back from the server rather than
// from the local session so the banner shows the same string the loser sees.
export const claim_state_from_response = (res: ClaimResponse | null | undefined): ClaimState => {
  const data = res?.data?.data || {};
  return {
    state: CLAIM_MINE,
    claimed_by_ops_uuid: data.claimed_by_ops_uuid || '',
    claimed_by_name: data.claimed_by_name || '',
    reason: ''
  };
};

const unclaimed = (state: ClaimStatus, reason: string): ClaimState => ({
  state,
  claimed_by_ops_uuid: '',
  claimed_by_name: '',
  reason
});

export const claim_state_from_error = (error: ClaimError | null | undefined): ClaimState => {
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  if (status === 409) {
    return {
      state: CLAIM_BLOCKED,
      claimed_by_ops_uuid: data.claimed_by_ops_uuid || '',
      claimed_by_name: data.claimed_by_name || '',
      // The server writes this sentence ("Already claimed by <name>"); falling
      // back to a name-less version rather than inventing a name.
      reason: data.reason || 'Already claimed by another rep'
    };
  }
  if (status === 403) return unclaimed(CLAIM_NOT_ALLOWED, data.reason || '');
  if (status === 410) return unclaimed(CLAIM_TERMINAL, data.reason || '');
  return unclaimed(CLAIM_FAILED, data.reason || '');
};

// The only state that takes the form away from the rep.
export const claim_blocks_form = (claim: Pick<ClaimState, 'state'> | null | undefined): boolean =>
  !!claim && claim.state === CLAIM_BLOCKED;

// A 410 means the row went terminal underneath us, so the form must render
// read-only even though the payload it loaded says otherwise.
export const claim_forces_terminal = (
  claim: Pick<ClaimState, 'state'> | null | undefined
): boolean => !!claim && claim.state === CLAIM_TERMINAL;

export const initial_claim_state = (): ClaimState => unclaimed(CLAIM_IDLE, '');

// Fired on window when this rep's claim proves a task is no longer pending, so
// the pop-up and the list drop it without waiting for a relay packet that a
// socket gap may have lost.
export const INSTANT_GONE_EVENT = 'sales-task:instant-gone';

export interface InstantGoneDetail {
  task_uuid: string;
}

export const announce_if_gone = (
  taskUuid: string | null | undefined,
  claim: Pick<ClaimState, 'state'> | null | undefined
): void => {
  if (!taskUuid || !claim) return;
  if (claim.state !== CLAIM_BLOCKED && claim.state !== CLAIM_TERMINAL) return;
  window.dispatchEvent(
    new CustomEvent<InstantGoneDetail>(INSTANT_GONE_EVENT, { detail: { task_uuid: taskUuid } })
  );
};
